import React, { useEffect } from 'react';
import { Button, Col, Container, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import axios from 'axios';
import API_URL from '../config/api';
import { cloudinaryAsset } from '../config/media';

const asset = (folder, filename) => cloudinaryAsset(`/exhibicion/${folder}/${filename}`);

const services = [
  {
    id: 'punto-de-venta',
    folder: 'exhibicion_punto_venta',
    images: [
      ['highlight', 'Destaque en punto de venta.jpg'],
      ['dumpBin', 'Botaderos.jpg'],
      ['counter', 'Mostrador promocional.png']
    ]
  },
  {
    id: 'exhibidores',
    folder: 'exhibidores',
    images: [
      ['easyClick', 'Exhibidores EasyClick.jpg'], ['counter', 'Exhibidores de mesa.jpg'],
      ['recycled', 'Exhibidores en material reciclable.jpg'], ['metal', 'Exhibidores metálicos.jpg'],
      ['custom', 'Exhibidores personalizados.jpg'], ['illuminated', 'Exhibidores rígidos luminosos.jpg'],
      ['rigid', 'Exhibidores rígidos.jpg'], ['islands', 'Islas.jpg']
    ]
  },
  {
    id: 'carteleria',
    folder: 'carteleria_señalizacion',
    images: [
      ['road', 'Carteleria de ruta.jpg'], ['illuminated', 'Cartelería luminosa.jpg'],
      ['flags', 'Cartelería saliente _ Bandera.jpg'], ['letters', 'Corpóreos.jpg'],
      ['neon', 'Iluminación neón.jpg'], ['acrylic', 'Luminosos de acrílico.jpg'],
      ['monopost', 'Monopostes.jpg'], ['wayfinding', 'Señalética.jpg'],
      ['totems', 'Tótems.jpg'], ['signage', 'img403.jpg']
    ]
  },
  {
    id: 'fachadas',
    folder: 'fachadas_comerciales',
    images: [
      ['facades', 'Fachadas en aluminio compuesto y acrilico.jpg'],
      ['acrylic', 'Luminosos de acrílico.jpg'], ['marquees', 'Marquesinas.jpg']
    ]
  },
  {
    id: 'grafica',
    folder: 'graficayploteo',
    images: [
      ['mesh', 'Lonas mesh.jpg'], ['perforated', 'Microperforados.jpg'],
      ['windows', 'Ploteo de vidrieras.jpg'], ['vehicles', 'Ploteo vehicular.jpg']
    ]
  }
];

const Carteleria = () => {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToContact = () => {
    document.getElementById('contact-form-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.target;
    const data = Object.fromEntries(new FormData(form).entries());

    try {
      await axios.post(`${API_URL}/contactos`, {
        asunto: 'Exhibition and signage - website enquiry',
        nombre: data.name,
        email: data.email,
        telefono: data.phone,
        mensaje: data.message
      });
      Swal.fire({
        title: t('contactForm.alert_title'),
        text: t('contactForm.alert_success'),
        icon: 'success',
        confirmButtonColor: '#1E5096'
      });
      form.reset();
    } catch (error) {
      console.error('Error sending message:', error);
      Swal.fire({
        title: t('common.error'),
        text: t('exhibitionPage.form.error'),
        icon: 'error',
        confirmButtonColor: '#1E5096'
      });
    }
  };

  const heroImage = asset(services[1].folder, services[1].images[2][1]);

  return (
    <main id="carteleria-page" className="exhibition-page">
      <section className="exhibition-hero" style={{ backgroundImage: `linear-gradient(90deg, rgba(8, 35, 71, .9) 0%, rgba(19, 71, 131, .75) 58%, rgba(19, 71, 131, .28) 100%), url("${heroImage}")` }}>
        <Container>
          <div className="exhibition-hero-content">
            <p className="exhibition-eyebrow">{t('exhibitionPage.hero.eyebrow')}</p>
            <h1>{t('exhibitionPage.hero.title')}</h1>
            <p>{t('exhibitionPage.hero.description')}</p>
            <Button className="exhibition-primary-button" onClick={scrollToContact}>{t('exhibitionPage.moreInformation')}</Button>
          </div>
        </Container>
      </section>

      <section className="exhibition-intro">
        <Container>
          <p>{t('exhibitionPage.intro')}</p>
          <nav className="exhibition-service-nav" aria-label={t('exhibitionPage.navigationLabel')}>
            {services.map(({ id }) => <a key={id} href={`#${id}`}>{t(`exhibitionPage.services.${id}.title`)}</a>)}
          </nav>
        </Container>
      </section>

      <section className="exhibition-services">
        <Container>
          {services.map((service, index) => (
            <article className={`exhibition-service ${index % 2 ? 'is-alternate' : ''}`} id={service.id} key={service.id}>
              <Row className="g-4 align-items-start">
                <Col lg={4}>
                  <div className="exhibition-service-copy">
                    <p className="exhibition-eyebrow">{t(`exhibitionPage.services.${service.id}.label`)}</p>
                    <h2>{t(`exhibitionPage.services.${service.id}.title`)}</h2>
                    <p className="exhibition-description">{t(`exhibitionPage.services.${service.id}.description`)}</p>
                    <dl>
                      <div>
                        <dt>{t('exhibitionPage.scope')}</dt>
                        <dd>{t(`exhibitionPage.services.${service.id}.scope`)}</dd>
                      </div>
                      <div>
                        <dt>{t('exhibitionPage.applications')}</dt>
                        <dd>{t(`exhibitionPage.services.${service.id}.applications`)}</dd>
                      </div>
                      <div>
                        <dt>{t('exhibitionPage.differential')}</dt>
                        <dd>{t(`exhibitionPage.services.${service.id}.differential`)}</dd>
                      </div>
                    </dl>
                    <Button variant="link" className="exhibition-text-button p-0" onClick={scrollToContact}>
                      {t('exhibitionPage.moreInformation')} <span aria-hidden="true">→</span>
                    </Button>
                  </div>
                </Col>
                <Col lg={8}>
                  <div className="exhibition-gallery">
                    {service.images.map(([captionKey, filename]) => (
                      <figure className="exhibition-gallery-item" key={filename}>
                        <img src={asset(service.folder, filename)} alt={t(`exhibitionPage.services.${service.id}.images.${captionKey}`)} loading="lazy" />
                        <figcaption>{t(`exhibitionPage.services.${service.id}.images.${captionKey}`)}</figcaption>
                      </figure>
                    ))}
                  </div>
                </Col>
              </Row>
            </article>
          ))}
        </Container>
      </section>

      <section id="contact-form-section" className="exhibition-contact">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8}>
              <div className="text-center mb-5">
                <p className="exhibition-eyebrow">{t('exhibitionPage.form.eyebrow')}</p>
                <h2>{t('exhibitionPage.form.title')}</h2>
                <p>{t('exhibitionPage.form.subtitle')}</p>
              </div>
              <Form onSubmit={handleSubmit} className="exhibition-contact-form">
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group controlId="formName">
                      <Form.Label>{t('contactForm.name')}</Form.Label>
                      <Form.Control type="text" name="name" placeholder={t('contactForm.name_placeholder')} required />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Group controlId="formPhone">
                      <Form.Label>{t('contactForm.phone')}</Form.Label>
                      <Form.Control type="tel" name="phone" placeholder={t('contactForm.phone_placeholder')} required />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3" controlId="formEmail">
                  <Form.Label>{t('contactForm.email')}</Form.Label>
                  <Form.Control type="email" name="email" placeholder={t('contactForm.email_placeholder')} required />
                </Form.Group>
                <Form.Group className="mb-4" controlId="formMessage">
                  <Form.Label>{t('contactForm.message')}</Form.Label>
                  <Form.Control as="textarea" name="message" rows={5} placeholder={t('exhibitionPage.form.messagePlaceholder')} required />
                </Form.Group>
                <div className="d-grid">
                  <Button className="exhibition-submit-button" type="submit" size="lg">{t('exhibitionPage.form.submit')}</Button>
                </div>
              </Form>
            </Col>
          </Row>
        </Container>
      </section>
    </main>
  );
};

export default Carteleria;
